/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authClientsCreate } from "../funcs/authClientsCreate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AuthClient } from "../models/components/authclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateAuthClientRequest,
  CreateAuthClientSecurity,
} from "../models/operations/createauthclient.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateAuthClientMutationVariables = {
  request: CreateAuthClientRequest;
  security?: CreateAuthClientSecurity | undefined;
  options?: RequestOptions;
};

export type CreateAuthClientMutationData = AuthClient;

export type CreateAuthClientMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createAuthClient authClients
 *
 * @remarks
 * Create an auth client and, optionally, offer it on MCP servers. A remote_session or service_account client authenticates through an existing OAuth registration whose credential owner matches its type; a static_credential or pass_through client authenticates with its own headers. Creating a client gives nobody permission to use it: set its audience to decide who may. Takes effect only where typed auth client resolution is enforced. Requires org:admin.
 */
export function useCreateAuthClientMutation(
  options?: MutationHookOptions<
    CreateAuthClientMutationData,
    CreateAuthClientMutationError,
    CreateAuthClientMutationVariables
  >,
): UseMutationResult<
  CreateAuthClientMutationData,
  CreateAuthClientMutationError,
  CreateAuthClientMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateAuthClientMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateAuthClient(): MutationKey {
  return ["@gram/client", "authClients", "create"];
}

export function buildCreateAuthClientMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateAuthClientMutationVariables,
  ) => Promise<CreateAuthClientMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateAuthClient(),
    mutationFn: function createAuthClientMutationFn({
      request,
      security,
      options,
    }): Promise<CreateAuthClientMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authClientsCreate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
